import { useEffect, useMemo, useRef, useState } from 'react';
import { Star, X, Loader2 } from 'lucide-react';
import apiService from '../../services/api';
import { useToast } from '../../hooks/useToast';
import Toast from '../Toast';
import {
  normalizeForMatch,
  SLUG_TO_BOOL,
} from '../../config/caracteristiqueLegacyBools.js';
import { normalizeCaracteristiqueChipItems } from './normalizeCaracteristiqueChipItems.js';

const MAX_ITEMS = 12;
const MAX_STAR = 3;
const MAX_LABEL_LEN = 48;
const DEBOUNCE_MS = 150;

const CODE_MESSAGES = {
  CAP_12: `Maximum ${MAX_ITEMS} caractéristiques atteint pour ce produit.`,
  CAP_STAR_3: `Maximum ${MAX_STAR} caractéristiques mises en avant.`,
  LABEL_TOO_LONG: `Ce libellé est trop long (${MAX_LABEL_LEN} caractères maximum).`,
  LABEL_EMPTY: 'Merci de saisir un libellé.',
  ARCHIVED: 'Cette caractéristique a été archivée.',
  NOT_FOUND: 'Caractéristique introuvable.',
  TENANT_MISMATCH: 'Ce produit ne correspond pas à ce commerçant.',
};

const normalizeInitialItems = normalizeCaracteristiqueChipItems;

/**
 * Normalise les réponses API (formes possibles : { link, caracteristique, created }
 * ou directement l'objet lien enrichi de `caracteristique`) en un item de chip.
 */
function normalizeLinkResponse(resp, fallbackOrdre = 0) {
  if (!resp) return null;
  // Forme route M1.4 : { item: { id, label, miseEnAvant, ordre }, created }
  if (resp.item && resp.item.id && resp.item.label) {
    return {
      id: resp.item.id,
      label: resp.item.label,
      miseEnAvant: Boolean(resp.item.miseEnAvant),
      ordre: resp.item.ordre ?? fallbackOrdre,
    };
  }
  const link = resp.link || resp;
  const carac = resp.caracteristique || link.caracteristique || null;
  const id = carac?.id || link?.caracteristiqueId || resp.id;
  const label = carac?.label || resp.label || link?.label;
  if (!id || !label) return null;
  return {
    id,
    label,
    miseEnAvant: Boolean(link?.miseEnAvant ?? resp.miseEnAvant ?? false),
    ordre: link?.ordre ?? resp.ordre ?? fallbackOrdre,
  };
}

function extractSuggestionList(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.items)) return res.items;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

/**
 * Chips « caractéristiques » — mémoire métier par commerçant (M2).
 * Ajout / retrait / mise en avant (⭐ max 3) reliés en direct à l'API,
 * sans jargon technique visible côté commerçant.
 */
export default function CaracteristiquesChips({
  commercantId,
  produitId,
  initialItems = [],
  /** Mentions historiques (booléens actifs sans chip) — reprise manuelle, pas de backfill. */
  legacyOrphans = [],
  onChange,
  onLegacyAdopted,
}) {
  const [items, setItems] = useState(() => normalizeInitialItems(initialItems));
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [submitting, setSubmitting] = useState(false);
  const [pendingItemId, setPendingItemId] = useState(null);
  const [adoptingField, setAdoptingField] = useState(null);

  const { toasts, toast, removeToast } = useToast();
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  // Resynchronise uniquement quand on change de produit (pas à chaque re-render du parent).
  useEffect(() => {
    setItems(normalizeInitialItems(initialItems));
    setInputValue('');
    setSuggestions([]);
    setShowSuggestions(false);
    setAdoptingField(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [produitId]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const coveredFields = useMemo(() => {
    const set = new Set();
    for (const it of items) {
      const field = SLUG_TO_BOOL[normalizeForMatch(it.label)];
      if (field) set.add(field);
    }
    return set;
  }, [items]);

  const visibleOrphans = useMemo(
    () =>
      (Array.isArray(legacyOrphans) ? legacyOrphans : []).filter(
        (o) => o?.field && o?.label && !coveredFields.has(o.field)
      ),
    [legacyOrphans, coveredFields]
  );

  function notifyChange(next) {
    if (typeof onChange === 'function') onChange(next);
  }

  function handleApiError(err, fallback = 'Une erreur est survenue.') {
    const code =
      err?.code ||
      err?.data?.error ||
      err?.data?.code ||
      err?.response?.data?.error ||
      err?.response?.data?.code;
    const message =
      (code && CODE_MESSAGES[code]) ||
      err?.data?.detail ||
      err?.response?.data?.detail ||
      err?.message ||
      fallback;
    toast.error(message);
  }

  async function fetchSuggestions(q) {
    if (!commercantId) return;
    setSuggestLoading(true);
    setShowSuggestions(true);
    try {
      const res = await apiService.listCommercantCaracteristiques({
        commercantId,
        q,
        limit: 8,
      });
      const linkedIds = new Set(items.map((it) => it.id));
      const list = extractSuggestionList(res).filter((s) => s && !linkedIds.has(s.id));
      setSuggestions(list);
    } catch (err) {
      setSuggestions([]);
    } finally {
      setSuggestLoading(false);
    }
  }

  function scheduleSuggestSearch(value) {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value.trim());
    }, DEBOUNCE_MS);
  }

  function handleInputChange(e) {
    const value = e.target.value;
    setInputValue(value);
    setHighlightedIndex(-1);
    scheduleSuggestSearch(value);
  }

  function handleInputFocus() {
    if (!inputValue.trim()) {
      fetchSuggestions('');
    } else {
      setShowSuggestions(true);
    }
  }

  function handleInputBlur() {
    // Laisse le temps au clic sur une suggestion de se déclencher avant fermeture.
    setTimeout(() => setShowSuggestions(false), 120);
  }

  async function addLink(payload, { legacyField = null } = {}) {
    if (items.length >= MAX_ITEMS) {
      toast.error(CODE_MESSAGES.CAP_12);
      return;
    }
    let body = { ...payload };
    if (body.caracteristiqueId) {
      body = { caracteristiqueId: body.caracteristiqueId };
    } else if (body.label) {
      const clean = String(body.label).trim();
      if (!clean) return;
      if (clean.length > MAX_LABEL_LEN) {
        toast.error(CODE_MESSAGES.LABEL_TOO_LONG);
        return;
      }
      body = { label: clean };
    } else {
      return;
    }

    setSubmitting(true);
    if (legacyField) setAdoptingField(legacyField);
    try {
      const resp = await apiService.addProduitCaracteristique(produitId, body);
      const normalized = normalizeLinkResponse(resp, items.length);
      if (normalized) {
        setItems((prev) => {
          if (prev.some((p) => p.id === normalized.id)) return prev;
          const next = [...prev, normalized];
          notifyChange(next);
          return next;
        });
        if (legacyField && typeof onLegacyAdopted === 'function') {
          onLegacyAdopted(legacyField);
        }
      }
      setInputValue('');
      setSuggestions([]);
      setShowSuggestions(false);
      setHighlightedIndex(-1);
    } catch (err) {
      handleApiError(err, "Impossible d'ajouter cette caractéristique.");
    } finally {
      setSubmitting(false);
      setAdoptingField(null);
      inputRef.current?.focus();
    }
  }

  function adoptLegacy(orphan) {
    if (!orphan?.label || submitting) return;
    addLink({ label: orphan.label }, { legacyField: orphan.field });
  }

  function handleKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!showSuggestions) {
        handleInputFocus();
        return;
      }
      setHighlightedIndex((idx) => (suggestions.length ? (idx + 1) % suggestions.length : -1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((idx) =>
        suggestions.length ? (idx - 1 + suggestions.length) % suggestions.length : -1
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (submitting) return;
      if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
        const s = suggestions[highlightedIndex];
        addLink({ caracteristiqueId: s.id, label: s.label });
      } else if (inputValue.trim()) {
        addLink({ label: inputValue.trim() });
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setHighlightedIndex(-1);
    }
  }

  async function toggleStar(item) {
    const nextValue = !item.miseEnAvant;
    if (nextValue) {
      const starCount = items.filter((it) => it.miseEnAvant).length;
      if (starCount >= MAX_STAR) {
        toast.error(CODE_MESSAGES.CAP_STAR_3);
        return;
      }
    }
    setPendingItemId(item.id);
    try {
      const resp = await apiService.patchProduitCaracteristique(produitId, item.id, {
        miseEnAvant: nextValue,
      });
      const normalized = normalizeLinkResponse(resp, item.ordre);
      setItems((prev) => {
        const next = prev.map((p) =>
          p.id === item.id
            ? { ...p, miseEnAvant: normalized?.miseEnAvant ?? nextValue }
            : p
        );
        notifyChange(next);
        return next;
      });
    } catch (err) {
      handleApiError(err, 'Impossible de mettre à jour la mise en avant.');
    } finally {
      setPendingItemId(null);
    }
  }

  async function removeItem(item) {
    setPendingItemId(item.id);
    try {
      await apiService.deleteProduitCaracteristique(produitId, item.id);
      setItems((prev) => {
        const next = prev.filter((p) => p.id !== item.id);
        notifyChange(next);
        return next;
      });
    } catch (err) {
      handleApiError(err, 'Impossible de retirer cette caractéristique.');
    } finally {
      setPendingItemId(null);
    }
  }

  const atMax = items.length >= MAX_ITEMS;

  return (
    <div className="space-y-2">
      {toasts.map((t) => (
        <Toast key={t.id} toast={t} onClose={() => removeToast(t.id)} />
      ))}

      {visibleOrphans.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 space-y-2">
          <p className="text-xs text-amber-900">
            Ce produit avait déjà ces mentions. Ajoutez-les en caractéristiques pour les conserver
            clairement sur la fiche.
          </p>
          <div className="flex flex-wrap gap-2">
            {visibleOrphans.map((orphan) => (
              <button
                key={orphan.field}
                type="button"
                disabled={submitting || atMax}
                onClick={() => adoptLegacy(orphan)}
                className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-white px-3 py-1 text-sm text-amber-900 hover:bg-amber-100 disabled:opacity-50"
              >
                {adoptingField === orphan.field ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : null}
                Ajouter « {orphan.label} »
              </button>
            ))}
          </div>
        </div>
      )}

      {items.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {items.map((item) => (
            <span
              key={item.id}
              className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-700"
            >
              <button
                type="button"
                onClick={() => toggleStar(item)}
                disabled={pendingItemId === item.id}
                aria-pressed={item.miseEnAvant}
                aria-label={
                  item.miseEnAvant
                    ? `Retirer la mise en avant de ${item.label}`
                    : `Mettre en avant ${item.label}`
                }
                className={`p-0.5 rounded transition disabled:opacity-50 ${
                  item.miseEnAvant
                    ? 'text-amber-500'
                    : 'text-gray-300 hover:text-amber-400'
                }`}
              >
                <Star className="h-3.5 w-3.5" fill={item.miseEnAvant ? 'currentColor' : 'none'} />
              </button>
              <span>{item.label}</span>
              <button
                type="button"
                onClick={() => removeItem(item)}
                disabled={pendingItemId === item.id}
                aria-label={`Retirer ${item.label}`}
                className="p-0.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition disabled:opacity-50"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      {atMax ? (
        <p className="text-xs text-gray-500">{CODE_MESSAGES.CAP_12}</p>
      ) : (
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={showSuggestions}
            aria-autocomplete="list"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            disabled={submitting || !produitId}
            placeholder="+ Ajouter une caractéristique"
            maxLength={MAX_LABEL_LEN}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-400"
          />
          {submitting && (
            <Loader2 className="h-4 w-4 animate-spin text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
          )}
          {showSuggestions && (suggestLoading || suggestions.length > 0) && (
            <div className="absolute z-20 mt-1 w-full max-h-56 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
              {suggestLoading ? (
                <div className="px-3 py-2 text-sm text-gray-500 flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Recherche…
                </div>
              ) : (
                suggestions.map((s, idx) => (
                  <button
                    key={s.id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    onClick={() => addLink({ caracteristiqueId: s.id, label: s.label })}
                    className={`w-full text-left px-3 py-2 text-sm transition ${
                      idx === highlightedIndex
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {s.label}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
